/**
 * MietwagenKorrespondenz — Korrespondenz-Tab in der Mietwagen-Akte
 * 
 * Features:
 * - Versand-Historie: Alle gesendeten/empfangenen E-Mails pro Akte
 * - E-Mail-Composer: Direkt aus der Akte E-Mails senden
 * - Vorlagen: Rundschreiben, Forderungsschreiben, Zahlungserinnerung etc.
 * - Status-Tracking: gesendet → zugestellt → beantwortet
 * - Ein-Klick-Versand: Alle Dokumente an Versicherung senden
 * - Automatische Verknüpfung eingehender Antworten
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Mail, Send, Inbox, ArrowUpRight, ArrowDownLeft, Clock, CheckCircle2,
  AlertCircle, FileText, Plus, X, ChevronDown, ChevronUp, RefreshCw,
  Building2, User, Loader2, Eye, Copy, Paperclip, MailOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { LCS_BRIEFKOPF } from '@/lib/lcs-briefkopf';

interface Korrespondenz {
  id: string;
  akte_id: string;
  akten_nr: string;
  richtung: 'ausgehend' | 'eingehend';
  typ: string;
  betreff: string;
  inhalt_text?: string;
  inhalt_html?: string;
  von_email: string;
  von_name?: string;
  an_email: string;
  an_name?: string;
  gmail_message_id?: string;
  gmail_thread_id?: string;
  vorlage?: string;
  kategorie: string;
  status: string;
  anhaenge?: any[];
  gesendet_am?: string;
  beantwortet_am?: string;
  erstellt_von?: string;
  created_at: string;
}

interface MietwagenKorrespondenzProps {
  akte: any;
  onUpdate?: () => void;
}

// ─── E-Mail-Vorlagen ─────────────────────────────────────────────────────────

const VORLAGEN = [
  {
    id: 'rundschreiben',
    label: 'Rundschreiben MietGo',
    kategorie: 'rundschreiben',
    betreff: 'Wichtige Mitteilung: Mietwagen-Service jetzt unter MietGo – LCS GmbH',
    getHtml: (akte: any) => `
<div style="font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #1a1a1a; line-height: 1.6;">
  <p>Sehr geehrte Damen und Herren,</p>
  <p>wir möchten Sie darüber informieren, dass unser Mietwagen-Service ab sofort unter der Marke <strong>MietGo</strong> firmiert. MietGo ist eine Marke der LCS GmbH.</p>
  <p><strong>Was ändert sich für Sie?</strong></p>
  <p>Unser Mietwagen-Service, den Sie bisher über Gutachter-Koop kannten, wird nun eigenständig unter dem Namen MietGo weitergeführt. Damit einher geht auch ein eigenes Forderungsmanagement, das wir ab sofort selbst betreiben.</p>
  <p><strong>Unsere neuen Kontaktdaten:</strong></p>
  <p style="margin-left: 20px;">
    LCS GmbH – MietGo<br/>
    ${LCS_BRIEFKOPF.anschrift}<br/><br/>
    E-Mail: info@mietgo.de
  </p>
  <p>Bitte richten Sie sämtliche Korrespondenz zu laufenden und zukünftigen Mietwagen-Vorgängen an die oben genannte Adresse bzw. E-Mail.</p>
  ${akte.schadennummer ? `<p>Betreffend: Schadennummer <strong>${akte.schadennummer}</strong>${akte.kunde_name ? `, Geschädigter: <strong>${akte.kunde_name}</strong>` : ''}</p>` : ''}
  <p>Wir freuen uns auf die weitere Zusammenarbeit und stehen Ihnen bei Rückfragen jederzeit gerne zur Verfügung.</p>
  <p>Mit freundlichen Grüßen</p>
  <p>
    <strong>${LCS_BRIEFKOPF.vertretungsberechtigt}</strong><br/>
    Vertretungsberechtigt<br/>
    LCS GmbH – MietGo<br/>
    ${LCS_BRIEFKOPF.anschrift}<br/>
    E-Mail: info@mietgo.de
  </p>
</div>`,
  },
  {
    id: 'versicherung_anschreiben',
    label: 'Versicherungs-Anschreiben (Forderung)',
    kategorie: 'versicherung_anschreiben',
    betreff: (akte: any) => `Mietwagenkosten – Schadennr. ${akte.schadennummer || 'N/A'} – ${akte.kunde_name || 'Geschädigter'}`,
    getHtml: (akte: any) => `
<div style="font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #1a1a1a; line-height: 1.6;">
  <p>Sehr geehrte Damen und Herren,</p>
  <p>in der Schadensache <strong>${akte.kunde_name || '—'}</strong> (Schadennummer: <strong>${akte.schadennummer || '—'}</strong>) erlauben wir uns, die Mietwagenkosten geltend zu machen.</p>
  <p>Der Geschädigte hat aufgrund des Unfallschadens ein Ersatzfahrzeug bei uns angemietet. Die Abtretungserklärung gemäß § 398 BGB liegt uns vor.</p>
  <p><strong>Mietdaten:</strong></p>
  <ul>
    <li>Mietbeginn: ${akte.mietbeginn ? new Date(akte.mietbeginn).toLocaleDateString('de-DE') : '—'}</li>
    <li>Mietende: ${akte.mietende ? new Date(akte.mietende).toLocaleDateString('de-DE') : '—'}</li>
    <li>Tagessatz: ${akte.tagespreis ? `${Number(akte.tagespreis).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '—'}</li>
  </ul>
  <p>Wir bitten um zeitnahe Regulierung der beigefügten Rechnung.</p>
  <p><strong>Zahlungsempfänger:</strong></p>
  <p style="margin-left: 20px;">
    LCS GmbH – MietGo<br/>
    ${LCS_BRIEFKOPF.anschrift}
  </p>
  <p>Für Rückfragen stehen wir Ihnen gerne zur Verfügung.</p>
  <p>Mit freundlichen Grüßen</p>
  <p>
    <strong>${LCS_BRIEFKOPF.vertretungsberechtigt}</strong><br/>
    Vertretungsberechtigt<br/>
    LCS GmbH – MietGo<br/>
    E-Mail: info@mietgo.de
  </p>
</div>`,
  },
  {
    id: 'zahlungserinnerung',
    label: 'Zahlungserinnerung',
    kategorie: 'zahlungserinnerung',
    betreff: (akte: any) => `Zahlungserinnerung – Schadennr. ${akte.schadennummer || 'N/A'} – ${akte.kunde_name || 'Geschädigter'}`,
    getHtml: (akte: any) => `
<div style="font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #1a1a1a; line-height: 1.6;">
  <p>Sehr geehrte Damen und Herren,</p>
  <p>wir erlauben uns, Sie an die noch offene Forderung in der Schadensache <strong>${akte.kunde_name || '—'}</strong> (Schadennummer: <strong>${akte.schadennummer || '—'}</strong>) zu erinnern.</p>
  <p>Trotz unseres Anschreibens konnten wir bislang keinen Zahlungseingang feststellen. Wir bitten Sie höflich, die Regulierung zeitnah vorzunehmen.</p>
  <p>Sollte die Zahlung bereits veranlasst worden sein, betrachten Sie dieses Schreiben bitte als gegenstandslos.</p>
  <p>Mit freundlichen Grüßen</p>
  <p>
    <strong>${LCS_BRIEFKOPF.vertretungsberechtigt}</strong><br/>
    Vertretungsberechtigt<br/>
    LCS GmbH – MietGo<br/>
    ${LCS_BRIEFKOPF.anschrift}<br/>
    E-Mail: info@mietgo.de
  </p>
</div>`,
  },
  {
    id: 'mietdauer_begruendung',
    label: 'Mietdauer-Begründung',
    kategorie: 'mietdauer_begruendung',
    betreff: (akte: any) => `Begründung der Mietdauer – Schadennr. ${akte.schadennummer || 'N/A'}`,
    getHtml: (akte: any) => `
<div style="font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #1a1a1a; line-height: 1.6;">
  <p>Sehr geehrte Damen und Herren,</p>
  <p>bezugnehmend auf Ihre Anfrage zur Mietdauer in der Schadensache <strong>${akte.kunde_name || '—'}</strong> (Schadennummer: <strong>${akte.schadennummer || '—'}</strong>) teilen wir Ihnen Folgendes mit:</p>
  <p><strong>Begründung der Mietdauer:</strong></p>
  <ul>
    ${!(akte.fahrzeug_verkehrssicher) ? '<li>Das beschädigte Fahrzeug war <strong>nicht verkehrssicher</strong> und durfte nicht am Straßenverkehr teilnehmen.</li>' : ''}
    ${!(akte.notreparatur_moeglich) ? '<li>Eine Notreparatur war <strong>nicht möglich</strong>, da die Beschädigungen eine fachgerechte Instandsetzung erforderten.</li>' : ''}
    ${akte.zerlegungsdauer_tage ? `<li>Für die Begutachtung mussten Verkleidungsteile (z.B. Stoßfänger) zerlegt werden. Die Zerlegungsdauer betrug <strong>${akte.zerlegungsdauer_tage} Tage</strong>.</li>` : ''}
    ${akte.voraussichtliche_reparaturdauer ? `<li>Die voraussichtliche Reparaturdauer beträgt <strong>${akte.voraussichtliche_reparaturdauer} Tage</strong> laut Schadengutachten.</li>` : ''}
    <li>Der Geschädigte hat gemäß § 249 BGB Anspruch auf Mobilität während der gesamten Reparatur- bzw. Wiederbeschaffungsdauer.</li>
  </ul>
  ${akte.mietdauer_begruendung ? `<p><strong>Ergänzende Begründung:</strong> ${akte.mietdauer_begruendung}</p>` : ''}
  <p>Wir bitten um Kenntnisnahme und zeitnahe Regulierung.</p>
  <p>Mit freundlichen Grüßen</p>
  <p>
    <strong>${LCS_BRIEFKOPF.vertretungsberechtigt}</strong><br/>
    Vertretungsberechtigt<br/>
    LCS GmbH – MietGo<br/>
    E-Mail: info@mietgo.de
  </p>
</div>`,
  },
];

// ─── Status-Helpers ──────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: any }> = {
  entwurf:       { label: 'Entwurf',       color: '#6B7280', bg: '#F3F4F6', icon: FileText },
  gesendet:      { label: 'Gesendet',      color: '#2563EB', bg: '#DBEAFE', icon: Send },
  zugestellt:    { label: 'Zugestellt',     color: '#059669', bg: '#D1FAE5', icon: CheckCircle2 },
  gelesen:       { label: 'Gelesen',        color: '#7C3AED', bg: '#EDE9FE', icon: MailOpen },
  beantwortet:   { label: 'Beantwortet',    color: '#059669', bg: '#D1FAE5', icon: Inbox },
  fehlgeschlagen:{ label: 'Fehlgeschlagen', color: '#DC2626', bg: '#FEE2E2', icon: AlertCircle },
};

// ─── Hauptkomponente ─────────────────────────────────────────────────────────

export default function MietwagenKorrespondenz({ akte, onUpdate }: MietwagenKorrespondenzProps) {
  const { session } = useAuth();
  const [korrespondenz, setKorrespondenz] = useState<Korrespondenz[]>([]);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [selectedVorlage, setSelectedVorlage] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  // Composer State
  const [composerTo, setComposerTo] = useState('');
  const [composerToName, setComposerToName] = useState('');
  const [composerSubject, setComposerSubject] = useState('');
  const [composerHtml, setComposerHtml] = useState('');
  const [composerKategorie, setComposerKategorie] = useState('allgemein');

  // Korrespondenz laden
  const loadKorrespondenz = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/akte-korrespondenz?action=getKorrespondenz&aktenNr=${akte.akten_nr}`, {
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      const data = await res.json();
      if (data.korrespondenz) {
        setKorrespondenz(data.korrespondenz);
      }
    } catch (err) {
      console.error('Korrespondenz laden fehlgeschlagen:', err);
    } finally {
      setLoading(false);
    }
  }, [akte.akten_nr]);

  useEffect(() => {
    loadKorrespondenz();
  }, [loadKorrespondenz]);

  // Vorlage anwenden
  const applyVorlage = (vorlageId: string) => {
    const vorlage = VORLAGEN.find(v => v.id === vorlageId);
    if (!vorlage) return;

    setSelectedVorlage(vorlageId);
    setComposerSubject(typeof vorlage.betreff === 'function' ? vorlage.betreff(akte) : vorlage.betreff);
    setComposerHtml(vorlage.getHtml(akte));
    setComposerKategorie(vorlage.kategorie);

    // Empfänger vorausfüllen
    if (akte.versicherung_email) {
      setComposerTo(akte.versicherung_email);
      setComposerToName(akte.versicherung || '');
    }

    setShowComposer(true);
  };

  // E-Mail senden
  const handleSend = async () => {
    if (!composerTo || !composerSubject) {
      toast.error('Bitte Empfänger und Betreff ausfüllen');
      return;
    }

    setSending(true);
    try {
      const res = await fetch('/api/akte-korrespondenz', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          action: 'sendFromAkte',
          akteId: akte.id,
          aktenNr: akte.akten_nr,
          an_email: composerTo,
          an_name: composerToName,
          betreff: composerSubject,
          inhalt_html: composerHtml,
          vorlage: selectedVorlage,
          kategorie: composerKategorie,
          erstellt_von: 'user',
        }),
      });

      const data = await res.json();

      if (data.success) {
        toast.success('E-Mail erfolgreich gesendet', {
          description: `An: ${composerTo}`,
        });
        setShowComposer(false);
        resetComposer();
        loadKorrespondenz();
        onUpdate?.();
      } else {
        toast.error('Versand fehlgeschlagen', {
          description: data.error || 'Unbekannter Fehler',
        });
      }
    } catch (err: any) {
      toast.error('Versand fehlgeschlagen', { description: err.message });
    } finally {
      setSending(false);
    }
  };

  const resetComposer = () => {
    setComposerTo('');
    setComposerToName('');
    setComposerSubject('');
    setComposerHtml('');
    setComposerKategorie('allgemein');
    setSelectedVorlage(null);
  };

  // Statistiken
  const stats = {
    gesamt: korrespondenz.length,
    ausgehend: korrespondenz.filter(k => k.richtung === 'ausgehend').length,
    eingehend: korrespondenz.filter(k => k.richtung === 'eingehend').length,
    offen: korrespondenz.filter(k => k.status === 'gesendet').length,
    beantwortet: korrespondenz.filter(k => k.status === 'beantwortet').length,
  };

  return (
    <div className="space-y-4">
      {/* ═══ HEADER MIT STATISTIKEN ═══ */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-[15px] font-bold text-gray-900">Korrespondenz</h3>
              <p className="text-[12px] text-gray-500">E-Mail-Versand und -Empfang für diese Akte</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadKorrespondenz}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Aktualisieren
            </button>
            <button
              onClick={() => { resetComposer(); setShowComposer(true); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-[11px] font-medium hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Neue E-Mail
            </button>
          </div>
        </div>

        {/* Statistik-Karten */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-[18px] font-bold text-gray-900">{stats.gesamt}</p>
            <p className="text-[10px] text-gray-500 font-medium">Gesamt</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-[18px] font-bold text-blue-700">{stats.ausgehend}</p>
            <p className="text-[10px] text-blue-600 font-medium">Gesendet</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-[18px] font-bold text-green-700">{stats.eingehend}</p>
            <p className="text-[10px] text-green-600 font-medium">Empfangen</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <p className="text-[18px] font-bold text-amber-700">{stats.offen}</p>
            <p className="text-[10px] text-amber-600 font-medium">Offen (ohne Antwort)</p>
          </div>
        </div>
      </div>

      {/* ═══ SCHNELL-VORLAGEN ═══ */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
        <h4 className="text-[13px] font-bold text-gray-900 mb-3">Schnell-Vorlagen</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {VORLAGEN.map(v => (
            <button
              key={v.id}
              onClick={() => applyVorlage(v.id)}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-left group"
            >
              <div className="w-8 h-8 bg-gray-100 group-hover:bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                <Send className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[12px] font-semibold text-gray-900 truncate">{v.label}</p>
                <p className="text-[10px] text-gray-500 truncate">
                  {typeof v.betreff === 'function' ? v.betreff(akte) : v.betreff}
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* Ein-Klick-Versand an Versicherung */}
        {akte.versicherung_email && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-amber-600" />
                <div>
                  <p className="text-[12px] font-semibold text-amber-900">{akte.versicherung || 'Versicherung'}</p>
                  <p className="text-[10px] text-amber-700">{akte.versicherung_email}</p>
                </div>
              </div>
              <button
                onClick={() => applyVorlage('versicherung_anschreiben')}
                className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white rounded-lg text-[11px] font-medium hover:bg-amber-700 transition-colors"
              >
                <Send className="w-3.5 h-3.5" />
                Forderung senden
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ═══ E-MAIL-COMPOSER ═══ */}
      {showComposer && (
        <div className="bg-white rounded-xl border-2 border-blue-300 p-4 sm:p-5 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-[14px] font-bold text-gray-900 flex items-center gap-2">
              <Send className="w-4 h-4 text-blue-600" />
              E-Mail verfassen
              {selectedVorlage && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                  {VORLAGEN.find(v => v.id === selectedVorlage)?.label}
                </span>
              )}
            </h4>
            <button
              onClick={() => { setShowComposer(false); resetComposer(); }}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-3">
            {/* An */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 mb-1">An (E-Mail)</label>
                <input
                  type="email"
                  value={composerTo}
                  onChange={e => setComposerTo(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-gray-50"
                  placeholder="empfaenger@versicherung.de"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 mb-1">Empfänger-Name</label>
                <input
                  type="text"
                  value={composerToName}
                  onChange={e => setComposerToName(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-gray-50"
                  placeholder="z.B. HUK-COBURG"
                />
              </div>
            </div>

            {/* Betreff */}
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 mb-1">Betreff</label>
              <input
                type="text"
                value={composerSubject}
                onChange={e => setComposerSubject(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-gray-50"
                placeholder="Betreff der E-Mail"
              />
            </div>

            {/* Vorschau */}
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 mb-1">Vorschau</label>
              <div
                className="w-full border border-gray-200 rounded-lg p-4 text-[13px] bg-white min-h-[200px] max-h-[400px] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: composerHtml || '<p style="color: #999;">Wählen Sie eine Vorlage oder verfassen Sie eine E-Mail...</p>' }}
              />
            </div>

            {/* Aktions-Buttons */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <div className="flex items-center gap-2 text-[11px] text-gray-500">
                <Mail className="w-3.5 h-3.5" />
                Wird gesendet über: info@gutachter-koop.de
                <span className="text-gray-300">|</span>
                Reply-To: info@mietgo.de
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowComposer(false); resetComposer(); }}
                  className="px-3 py-2 rounded-lg text-[11px] font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending || !composerTo || !composerSubject}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-[12px] font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Jetzt senden
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ KORRESPONDENZ-HISTORIE ═══ */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
        <h4 className="text-[13px] font-bold text-gray-900 mb-3">Verlauf</h4>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            <span className="ml-2 text-[12px] text-gray-500">Korrespondenz wird geladen...</span>
          </div>
        ) : korrespondenz.length === 0 ? (
          <div className="text-center py-8">
            <Mail className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-[13px] text-gray-500">Noch keine Korrespondenz für diese Akte</p>
            <p className="text-[11px] text-gray-400 mt-1">Senden Sie die erste E-Mail über eine der Vorlagen oben</p>
          </div>
        ) : (
          <div className="space-y-2">
            {korrespondenz.map(k => {
              const statusCfg = STATUS_CONFIG[k.status] || STATUS_CONFIG.gesendet;
              const StatusIcon = statusCfg.icon;
              const isExpanded = expandedId === k.id;
              const isOutgoing = k.richtung === 'ausgehend';

              return (
                <div
                  key={k.id}
                  className={`rounded-lg border transition-all ${
                    isExpanded ? 'border-blue-200 shadow-sm' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : k.id)}
                    className="w-full flex items-center gap-3 p-3 text-left"
                  >
                    {/* Richtungs-Icon */}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isOutgoing ? 'bg-blue-50' : 'bg-green-50'
                    }`}>
                      {isOutgoing ? (
                        <ArrowUpRight className="w-4 h-4 text-blue-600" />
                      ) : (
                        <ArrowDownLeft className="w-4 h-4 text-green-600" />
                      )}
                    </div>

                    {/* Inhalt */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-[12px] font-semibold text-gray-900 truncate">{k.betreff || '(Kein Betreff)'}</p>
                        <span
                          className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                          style={{ color: statusCfg.color, background: statusCfg.bg }}
                        >
                          {statusCfg.label}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 truncate mt-0.5">
                        {isOutgoing ? `An: ${k.an_email}` : `Von: ${k.von_email}`}
                        {k.gesendet_am && ` · ${new Date(k.gesendet_am).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                      </p>
                    </div>

                    {/* Expand-Icon */}
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                  </button>

                  {/* Expandierter Inhalt */}
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-gray-100">
                      <div className="grid grid-cols-2 gap-2 mt-3 mb-3">
                        <div className="bg-gray-50 rounded-lg p-2">
                          <p className="text-[10px] text-gray-500">Von</p>
                          <p className="text-[11px] font-medium text-gray-900">{k.von_name || k.von_email}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-2">
                          <p className="text-[10px] text-gray-500">An</p>
                          <p className="text-[11px] font-medium text-gray-900">{k.an_name || k.an_email}</p>
                        </div>
                        {k.vorlage && (
                          <div className="bg-gray-50 rounded-lg p-2">
                            <p className="text-[10px] text-gray-500">Vorlage</p>
                            <p className="text-[11px] font-medium text-gray-900">{k.vorlage}</p>
                          </div>
                        )}
                        {k.beantwortet_am && (
                          <div className="bg-green-50 rounded-lg p-2">
                            <p className="text-[10px] text-green-600">Beantwortet am</p>
                            <p className="text-[11px] font-medium text-green-900">
                              {new Date(k.beantwortet_am).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* E-Mail-Inhalt */}
                      {k.inhalt_html && (
                        <div
                          className="bg-gray-50 rounded-lg p-3 text-[12px] text-gray-700 max-h-[300px] overflow-y-auto"
                          dangerouslySetInnerHTML={{ __html: k.inhalt_html }}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
